import { MappingIPA } from "@/constants/mappingIPA";

export interface IPhonemeLike {
  phone?: string;
  qualityScore?: number;
  quality_score?: number;
  score?: number;
  soundMostLike?: string;
  sound_most_like?: string;
}

export const ARPABET_VOWELS = new Set([
  "aa",
  "ae",
  "ah",
  "ao",
  "aw",
  "ay",
  "eh",
  "er",
  "ey",
  "ih",
  "iy",
  "ow",
  "oy",
  "uh",
  "uw",
  "ax",
  "ix",
  "ux",
  "axr",
]);

export const IPA_VOWELS_SET = new Set([
  "ɑ",
  "æ",
  "ə",
  "ʌ",
  "ɔ",
  "aʊ",
  "aɪ",
  "ɛ",
  "ɜ",
  "eɪ",
  "ɪ",
  "i",
  "iː",
  "uː",
  "oʊ",
  "ɔɪ",
  "ʊ",
  "u",
  "ɒ",
  "e",
  "o",
  "a",
  "ɐ",
  "ɝ",
  "ɚ",
  "ɪə",
  "eə",
  "ʊə",
  "aː",
  "ɔː",
  "ɑː",
  "ɜː",
]);

/**
 * Checks whether a given phoneme string (ARPAbet or IPA) represents a vowel.
 */
export const isVowelPhoneme = (phone?: string): boolean => {
  if (!phone) return false;
  const clean = phone.toLowerCase().trim().replace(/\d+$/, "");
  if (ARPABET_VOWELS.has(clean)) return true;

  const mappedIpa = MappingIPA[clean as keyof typeof MappingIPA] || clean;
  if (IPA_VOWELS_SET.has(clean) || IPA_VOWELS_SET.has(mappedIpa)) return true;

  const vowelCharRegex = /[ɑæəʌɔɛɜɪiʊuɒeoɐɝɚ]/;
  return vowelCharRegex.test(mappedIpa) || vowelCharRegex.test(clean);
};

/**
 * Returns true if at least one vowel phoneme in the word has poor quality (< 60)
 * or was mispronounced.
 */
export const hasVowelPronunciationError = (phonemes?: IPhonemeLike[] | null): boolean => {
  if (!phonemes || !Array.isArray(phonemes) || phonemes.length === 0) return false;

  return phonemes.some((p) => {
    const phone = p.phone || "";
    if (!isVowelPhoneme(phone)) return false;

    const score = p.qualityScore ?? p.quality_score ?? p.score;
    if (score !== undefined && score !== null) {
      return score < 60;
    }
    if (p.soundMostLike || p.sound_most_like) {
      return true;
    }
    return false;
  });
};

/**
 * Calculates the color code for a word based on its score and vowel accuracy.
 * Rule: If any vowel phoneme is mispronounced (< 60), the word is immediately marked RED (error).
 */
export const getWordPronunciationColor = (
  wordObj?: {
    score?: number;
    quality_score?: number;
    phonemes?: IPhonemeLike[];
    phone_score_list?: any;
  } | null,
  options?: {
    goodColor?: string;
    warningColor?: string;
    errorColor?: string;
  }
): string => {
  const good = options?.goodColor || "#10B981";
  const warning = options?.warningColor || "#F59E0B";
  const error = options?.errorColor || "#EF4444";

  if (!wordObj) return good;

  let phonemes = wordObj.phonemes;
  if (!phonemes && wordObj.phone_score_list) {
    phonemes = Array.isArray(wordObj.phone_score_list)
      ? (wordObj.phone_score_list as any[]).flat()
      : undefined;
  }

  // If any vowel is wrong, mark red directly
  if (hasVowelPronunciationError(phonemes)) {
    return error;
  }

  const score = wordObj.score ?? wordObj.quality_score ?? 0;
  if (score >= 80) return good;
  if (score >= 60) return warning;
  return error;
};
